import { RegisterItemColumn } from '@core/entities/register/RegisterItemColumn'
import {
  AdvancedDateFilterValue,
  advancedDateFilterValueIsPartial,
  AdvancedDiagramFilterValue,
  advancedDiagramFilterValueIsPartial,
  AdvancedDueDateFilterValue,
  advancedDueDateFilterValueIsPartial,
  AdvancedNumberFilterValue,
  advancedNumberFilterValueIsPartial,
  AdvancedSelectFilterValue,
  advancedSelectFilterValueIsPartial,
  advancedTextFilterValueIsPartial,
  AdvancedTextInputFilterValue,
  AdvancedTimeFilterValue,
  advancedTimeFilterValueIsPartial,
  AdvancedUserFilterValue,
  advancedUserFilterValueIsPartial,
  permiTemplateFilterValueIsPartial,
  permitStatusFilterValueIsPartial,
} from '@core/providers/filters'
import { PermitStatus } from '@core/entities/permit/PermitBase'
import { registerTypes } from '../RegisterTemplateProperty/types'
import { TaskStatus } from '@core/entities/task/TaskBase'

export const getRegisterItemsFilterQueryValues = (
  registerItemsColumns: Array<RegisterItemColumn> = [],
  values: { [key: string | number]: unknown },
) => {
  return registerItemsColumns.reduce((result, property) => {
    const filterQuery = []

    switch (property.type) {
      case 'STRING':
        const stringFilterValue = values[
          property.id
        ] as AdvancedTextInputFilterValue

        if (advancedTextFilterValueIsPartial(stringFilterValue)) {
          return result
        }

        filterQuery.push({
          propertyId: property.id,
          propertyType: registerTypes.STRING,
          operator: stringFilterValue?.operator,
          textValue: stringFilterValue?.value,
        })
        break
      case 'SELECT':
        const selectFilterValue = values[
          property.id
        ] as AdvancedSelectFilterValue

        if (advancedSelectFilterValueIsPartial(selectFilterValue)) {
          return result
        }

        filterQuery.push({
          propertyId: property.id,
          propertyType: registerTypes.SELECT,
          operator: selectFilterValue?.operator,
          selectedOptionUuids: selectFilterValue?.value,
        })
        break
      case 'DATE':
        const dateFilterValue = values[property.id] as AdvancedDateFilterValue

        if (advancedDateFilterValueIsPartial(dateFilterValue)) {
          return result
        }

        filterQuery.push({
          propertyId: property.id,
          propertyType: registerTypes.DATE,
          operator: dateFilterValue?.operator,
          startDate: dateFilterValue?.value?.startTime ?? '',
          endDate: dateFilterValue?.value?.endTime ?? '',
          dateValue: dateFilterValue?.value?.dateValue ?? '',
        })
        break
      case 'NUMBER':
        const numberFilterValue = values[
          property.id
        ] as AdvancedNumberFilterValue

        if (advancedNumberFilterValueIsPartial(numberFilterValue)) {
          return result
        }

        filterQuery.push({
          propertyId: property.id,
          propertyType: registerTypes.NUMBER,
          operator: numberFilterValue?.operator,
          numberValue: numberFilterValue?.value,
        })
        break
      case 'INCREMENTAL_ID':
        const incrementalFilterValue = values[
          property.id
        ] as AdvancedTextInputFilterValue

        if (advancedTextFilterValueIsPartial(incrementalFilterValue)) {
          return result
        }

        filterQuery.push({
          propertyId: property.id,
          propertyType: registerTypes.STRING,
          operator: incrementalFilterValue?.operator,
          textValue: incrementalFilterValue?.value,
        })
        break
      case 'USER':
        const userFilterValue = values[property.id] as AdvancedUserFilterValue
        if (advancedUserFilterValueIsPartial(userFilterValue)) {
          return result
        }

        filterQuery.push({
          propertyId: property.id,
          propertyType: registerTypes.USER,
          operator: userFilterValue?.operator,
          userId: userFilterValue?.value,
        })
        break
      case 'DIAGRAM':
        const diagramFilterValue = values[
          property.id
        ] as AdvancedDiagramFilterValue

        if (advancedDiagramFilterValueIsPartial(diagramFilterValue)) {
          return result
        }

        filterQuery.push({
          propertyId: property.id,
          propertyType: registerTypes.DIAGRAM,
          operator: diagramFilterValue?.operator,
          annotationSetId: diagramFilterValue?.value,
        })
        break
      case 'INSPECTION':
        const dueDateFilterValue = values[
          property.id
        ] as AdvancedDueDateFilterValue

        if (advancedDueDateFilterValueIsPartial(dueDateFilterValue)) {
          return result
        }

        filterQuery.push({
          propertyId: property.id,
          propertyType: registerTypes.INSPECTION,
          operator: dueDateFilterValue?.operator,
          numberOfDays: dueDateFilterValue?.value,
        })
        break
      case 'TIME':
        const timeFilterValue = values[property.id] as AdvancedTimeFilterValue

        if (advancedTimeFilterValueIsPartial(timeFilterValue)) {
          return result
        }

        filterQuery.push({
          propertyId: property.id,
          propertyType: registerTypes.TIME,
          operator: timeFilterValue?.operator,
          timeValue: timeFilterValue?.value?.timeValue ?? '',
          startTime: timeFilterValue?.value?.startTime ?? '',
          endTime: timeFilterValue?.value?.endTime ?? '',
        })
        break
      case 'PERMIT':
        const permitStatusFilterValue = values[
          property.id + '-status'
        ] as Array<PermitStatus>
        const permitTemplateFilterValue = values[
          property.id + '-template'
        ] as Array<number>

        const isPermitStatusIsPartial = permitStatusFilterValueIsPartial(
          permitStatusFilterValue,
        )
        const isPermitTemplateIsPartial = permiTemplateFilterValueIsPartial(
          permitTemplateFilterValue,
        )

        // If both filters are partial/empty, skip this property
        if (isPermitStatusIsPartial && isPermitTemplateIsPartial) {
          return result
        }

        // Add status filter if it's not partial
        if (!isPermitStatusIsPartial) {
          filterQuery.push({
            propertyId: property.id + '-status',
            propertyType: registerTypes.PERMIT,
            operator: 'HAS_STATUS',
            statuses: permitStatusFilterValue,
          })
        }

        // Add template filter if it's not partial
        if (!isPermitTemplateIsPartial) {
          filterQuery.push({
            propertyId: property.id + '-template',
            propertyType: registerTypes.PERMIT,
            operator: 'HAS_TEMPLATE',
            permitTemplateIds: permitTemplateFilterValue,
          })
        }
        break
      case 'TASK':
        const taskStatusFilterValue = values[
          property.id + '-status'
        ] as Array<TaskStatus>
        const taskDueDateFilterValue = values[
          property.id + '-dueDate'
        ] as AdvancedDueDateFilterValue

        const taskStatusFilterValueIsPartial = (value: Array<TaskStatus>) => {
          if (!value?.length) {
            return true
          }
          return false
        }

        const isTaskStatusIsPartial = taskStatusFilterValueIsPartial(
          taskStatusFilterValue,
        )
        const isTaskDueDateIsPartial = advancedDueDateFilterValueIsPartial(
          taskDueDateFilterValue,
        )

        // If both filters are partial/empty, skip this property
        if (isTaskStatusIsPartial && isTaskDueDateIsPartial) {
          return result
        }

        // Add status filter if it's not partial
        if (!isTaskStatusIsPartial) {
          filterQuery.push({
            propertyId: property.id + '-status',
            propertyType: registerTypes.TASK,
            operator: 'HAS_STATUS',
            statuses: taskStatusFilterValue,
          })
        }

        // Add template filter if it's not partial
        if (!isTaskDueDateIsPartial) {
          filterQuery.push({
            propertyId: property.id + '-dueDate',
            propertyType: registerTypes.TASK,
            operator: taskDueDateFilterValue?.operator,
            numberOfDays: taskDueDateFilterValue?.value,
          })
        }
        break
      default:
        return result
    }

    return [...result, ...filterQuery]
  }, [])
}
